import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { debounce } from 'lodash';
import { setColorType } from 'PDF/helpers/documentStyle';
import { useUpdateBusinessPlan } from 'services/businessPlan/hooks';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getSetBusinessPlan } from 'store/useBusinessToolStore/selectors';

import { useAuthenticated } from 'hooks/useAuthenticated';
import { useIsBusinessPlanOwner } from 'hooks/useIsBusinessPlanOwner';

import { IBusinessPlanEntity } from 'interfaces/businessPlan';

import { getValidatedMetadata } from 'helpers/metadataParser';
import ToastService from 'helpers/ToastService';

import { IColorOptionUpdateParams, IDocumentStyleUpdateParams, IHookParameterTypes } from './types';

export const useDebounceUpdateMetadataHandler = <T extends IColorOptionUpdateParams | IDocumentStyleUpdateParams>({
  metaDataKey,
  errorMessage,
}: Omit<IHookParameterTypes<T>, 'type'>) => {
  const { t } = useTranslation();

  const updateMutation = useUpdateBusinessPlan();

  const setBusinessPlan = useBusinessToolStore(getSetBusinessPlan);

  const { isAuthenticated } = useAuthenticated();
  const isBusinessPlanOwner = useIsBusinessPlanOwner();

  const debouncedHandler = useMemo(
    () =>
      debounce(
        async (
          type: IHookParameterTypes<T>['type'],
          businessPlan: IBusinessPlanEntity,
          selectedType: IHookParameterTypes<T>['type'],
        ) => {
          setBusinessPlan({
            ...businessPlan,
            metadata: { ...getValidatedMetadata(businessPlan.metadata), [metaDataKey]: type },
          });

          if (!isAuthenticated || !isBusinessPlanOwner) {
            return;
          }

          const params = {
            id: businessPlan.uuid,
            body: {
              metadata: JSON.stringify({ ...getValidatedMetadata(businessPlan.metadata), [metaDataKey]: type }),
            },
          };

          try {
            await updateMutation.mutateAsync(params);
          } catch (error) {
            setBusinessPlan({
              ...businessPlan,
              metadata: { ...getValidatedMetadata(businessPlan.metadata), [metaDataKey]: selectedType },
            });
            setColorType(businessPlan.metadata.documentColorType);

            return ToastService.showError(t(errorMessage));
          }
        },
        500,
      ),

    [],
  );
  return debouncedHandler;
};
